import { mdiClose } from '@mdi/js'
import Icon from '@mdi/react'
import TextEditor from 'components/TextEditor'
import { Feed } from 'interfaces'
import { Fragment, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { Observable } from 'rxjs'
import { removeComment, removeMultipostComment, setComment, setMultipostComment, setMultipostCommentDelay } from 'services/compose'
import { checkFeatureAvailability, getProductUpgradeByFeature, promptUpgrade } from 'services/product'
import { FEATURE_POST_COMMENTS, PostComment, BRAND_FACEBOOK } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import { DelaySelect } from './CommentTextBox'
import styles from './FacebookCommentTextBox.pcss'

interface FacebookCommentTextBoxProps {
  comment: PostComment
  multiPostId?: string
  className?: string
  showRemoveButton?: boolean
  tagSearchFn: (needle: string) => Observable<Feed[]>
}
export function FacebookCommentTextBox({
  comment, multiPostId, className, showRemoveButton, tagSearchFn
}: FacebookCommentTextBoxProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()

  const commentsAllowed = useMemo(() => dispatch(checkFeatureAvailability(FEATURE_POST_COMMENTS, true)), [dispatch])
  const commentsProductUpgrade = useMemo(() => dispatch(getProductUpgradeByFeature(FEATURE_POST_COMMENTS)), [dispatch])

  const handleDelayChange = (delay: number) => {
    if (multiPostId) {
      dispatch(setMultipostCommentDelay({ postId: multiPostId, commentId: comment.id, delay }))
      return
    }
    dispatch(setComment({
      network: BRAND_FACEBOOK,
      comment: { delay, id: comment.id }
    }))
  }

  // EXPL: Block input before it reaches the DOM when the feature is gated.
  // Returning false cancels the keystroke/paste/emoji; we also surface the upgrade prompt.
  const handleBeforeInput = () => {
    if (!commentsAllowed && commentsProductUpgrade) {
      dispatch(promptUpgrade({ feature: FEATURE_POST_COMMENTS, product: commentsProductUpgrade?.handles.annual }))
      return false
    }
    return true
  }

  const onChange = (text: string, html: string) => {
    if (!commentsAllowed && commentsProductUpgrade) {
      dispatch(promptUpgrade({ feature: FEATURE_POST_COMMENTS, product: commentsProductUpgrade?.handles.annual }))
      return
    }

    if (multiPostId) {
      dispatch(setMultipostComment({ postId: multiPostId, comment: { id: comment.id, text, html } }))
      return
    }
    dispatch(setComment({
      network: BRAND_FACEBOOK,
      comment: { id: comment.id, text, html }
    }))
  }

  const handleRemoveComment = () => {
    if (multiPostId) {
      dispatch(removeMultipostComment({ postId: multiPostId, commentId: comment.id }))
      return
    }
    dispatch(removeComment({ network: BRAND_FACEBOOK, commentId: comment.id }))
  }

  return (
    <div className={`${styles.wrapper} ${className || ''}`}>
      <TextEditor
        network={BRAND_FACEBOOK}
        initialHTML={comment.html}
        containerClassName={styles['fbfc-container']}
        inputClassName={styles['fbfc-input']}
        footerClassName={styles['fbfc-footer']}
        withEmojis
        footerElements={(
          <Fragment>
            <DelaySelect
              delay={comment.delay || 0}
              commentId={comment.id}
              className={styles['delay-select']}
              onChange={handleDelayChange}
            />
            {showRemoveButton && (
              <span className={styles['btn-delete']} onClick={handleRemoveComment}>
                <Icon path={mdiClose} size="22px" className={styles.icon} />
              </span>
            )}
          </Fragment>
        )}
        placeholder={intl.formatMessage({ id: 'composer.placeholder.comment' })}
        searchTags={tagSearchFn}
        onBeforeInput={handleBeforeInput}
        onChange={onChange}
      />
    </div>
  )
}
